import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Clock, AlertTriangle } from "lucide-react";
import SEOHead from "../components/layout/SEOHead";
import { useCart, fmt } from "../context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;
const cents = (c) => (c == null ? "—" : fmt(c / 100));

const OrderConfirmationPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("polling");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API}/api/checkout/status/${sessionId}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setOrder(data);
        if (data.payment_status === "paid") { setState("paid"); clear(); return; }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts < 8) timer = setTimeout(poll, 2000); else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <>
      <SEOHead title="Order Confirmation | Highwall Lift" description="Your Highwall Lift order." canonicalPath="/order/confirmation" />
      <div className="min-h-screen bg-[#0A0A0A] pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          {state === "polling" && <div className="text-center py-20" data-testid="order-polling"><Clock className="w-10 h-10 text-signal-red mx-auto mb-4 animate-pulse" /><p className="text-gray-300">Confirming your payment…</p></div>}

          {state === "paid" && order && (
            <div data-testid="order-confirmed">
              <CheckCircle className="w-12 h-12 text-signal-red mb-4" />
              <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-xs font-bold mb-2">Order Confirmed</p>
              <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white mb-2">Thank You</h1>
              <p className="text-gray-300 mb-8" data-testid="order-confirmed-copy">Order <strong className="text-white" data-testid="order-number">{order.order_number}</strong> has been received and paid. {order.confirmation_email_sent
                ? <>A confirmation has been sent to <span className="text-white">{order.email}</span>.</>
                : <>Your order details are shown below — please save your order number for reference.</>} We ship pronto — you'll receive tracking when it leaves Amarillo.</p>
              <div className="bg-[#141414] border border-white/10 p-6">
                <ul className="divide-y divide-white/10 mb-4">
                  {order.items.map((l) => (
                    <li key={l.sku} className="py-3 flex justify-between gap-3" data-testid={`order-line-${l.sku}`}>
                      <span className="text-gray-300">{l.name} <span className="text-gray-500">({l.model}) × {l.quantity} {l.unit}{l.quantity === 1 ? "" : "s"}</span></span>
                      <span className="text-white font-bold">{cents(l.line_total_cents)}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-300"><span>Subtotal</span><span>{cents(order.subtotal_cents)}</span></div>
                  <div className="flex justify-between text-gray-300"><span>Shipping ({order.shipping_service})</span><span>{cents(order.shipping_cents)}</span></div>
                  <div className="flex justify-between text-gray-300"><span>Tax</span><span>{cents(order.tax_cents)}</span></div>
                  <div className="flex justify-between text-white font-heading text-xl font-black pt-3 border-t border-white/10"><span>Total</span><span data-testid="order-total">{cents(order.total_cents)}</span></div>
                </div>
              </div>
            </div>
          )}

          {state === "pending" && (
            <div className="text-center py-20" data-testid="order-pending">
              <Clock className="w-10 h-10 text-signal-red mx-auto mb-4" />
              <h1 className="font-heading text-3xl font-bold text-white uppercase mb-3">Payment Processing</h1>
              <p className="text-gray-300" data-testid="order-pending-copy">We haven't received final confirmation from Stripe yet. Your order{order?.order_number ? ` ${order.order_number}` : ""} will be confirmed as soon as payment is verified — refresh this page in a moment, or contact Highwall at (806) 729-3701 with your order number. Nothing further is needed from you.</p>
            </div>
          )}

          {(state === "failed" || state === "error") && (
            <div className="text-center py-20" data-testid="order-failed">
              <AlertTriangle className="w-10 h-10 text-signal-red mx-auto mb-4" />
              <h1 className="font-heading text-3xl font-bold text-white uppercase mb-3">Payment Not Completed</h1>
              <p className="text-gray-300 mb-6">No charge was made. Your cart has been kept so you can try again or request a quote.</p>
              <Link to="/cart" className="inline-flex px-6 py-3 bg-signal-red text-white font-bold uppercase text-sm" data-testid="order-back-to-cart">Return to Cart</Link>
            </div>
          )}

          <div className="mt-10 flex gap-6 text-sm">
            <Link to="/" className="text-gray-400 hover:text-white uppercase tracking-wide">Home</Link>
            <Link to="/#contact" className="text-gray-400 hover:text-white uppercase tracking-wide">Contact Highwall</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderConfirmationPage;
